import { useMemo } from "react";
import { useLingui } from "@lingui/react";
import type { LocalizedMessage } from "../types";

/**
 * Hook for resolving localized messages with parameter interpolation.
 * Translates resource names and main message using Lingui.
 * @param message - LocalizedMessage object with key and params
 * @returns Resolved message string with interpolated parameters
 */
export function useLocalizedSyncMessage(
  message: LocalizedMessage | null | undefined,
): string {
  const { i18n } = useLingui();

  return useMemo(() => {
    if (!message) return "";

    try {
      const params = { ...(message.params ?? {}) };

      // Translate resource label if present
      if (params.resourceKey && params.integrationKey) {
        const resourceId = `resource.${params.integrationKey}.${params.resourceKey}`;
        const translated = i18n._(resourceId as any);

        // If translation found (doesn't return the id itself), use it
        params.resource =
          translated !== resourceId ? translated : params.resourceKey;

        // Remove resourceKey since we're using resource instead
        delete params.resourceKey;
        delete params.integrationKey;
      }

      // Translate the main message
      return i18n._(message.key as any, params);
    } catch (error) {
      console.warn(
        `[useLocalizedSyncMessage] Failed to translate "${message.key}":`,
        error,
      );
      return message.key; // Fallback to key if translation fails
    }
  }, [message, i18n]);
}
